<template>
  <div class="card shadow-sm h-100">
    <div class="card-body">
      <div class="d-flex justify-content-between align-items-center mb-15">
        <h2 class="card-title text-center  mb-0">
          {{ title }}
        </h2>
        <div class="dropdown">
          <button class="btn btn-sm btn-icon" type="button" data-bs-toggle="dropdown" aria-expanded="false">
            <i class="fas fa-ellipsis-v"></i>
          </button>
          <ul class="dropdown-menu" style="min-width: auto; width: 40px;">
            <li><a class="dropdown-item" @click="changeChartType('doughnut')"><i class="fas fa-chart-pie"></i></a></li>
            <li><a class="dropdown-item" @click="changeChartType('bar')"><i class="fas fa-chart-bar"></i></a></li>
          </ul>
        </div>
      </div>
      <div class="chart-container" style="height: 400px">
        <canvas :id="chartId" :key="chartKey"></canvas>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import Chart from 'chart.js/auto'
import {useI18n} from "vue-i18n";
const { t } = useI18n({ useScope: 'global' })

const props = defineProps({
  surveyReport: { type: Object, required: true },
  selectedCategory: { type: String, required: true },
  selectedPopulation: { type: String, required: true },
  title: { type: String, default: 'Répartition par population' }
})

const chartId = computed(() => `population-chart-${props.selectedCategory}`)
const chartType = ref('doughnut')
const chart = ref(null)

const chartKey = computed(() => `population-${props.selectedCategory}-${props.selectedPopulation}-${chartType.value}`)

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend:{ display: false },
    tooltip: { callbacks: { label: function(context) { return `${context.dataset.label || context.label}: ${context.raw}` } } }
  }
}

const doughnutOptions = {
  ...chartOptions,
  cutout: '60%',
  layout: {
    padding: {
      left: 10,
      right: 10,
      top: 20,
      bottom: 20
    }
  },
  plugins: {
    ...chartOptions.plugins,
    legend: {
      position: 'right',
      labels: {
        usePointStyle: false,
        boxWidth: 45,
        boxHeight: 8,
        padding: 30,
        color: '#000',
        font: { size: 18 },
        generateLabels: function(chart) {
          const data = chart.data;
          if (data.labels.length && data.datasets.length) {
            const dataset = data.datasets[0];
            const total = dataset.data.reduce((sum, val) => sum + val, 0);

            let rawPercents = dataset.data.map(v => (v * 100) / total);
            let roundedPercents = rawPercents.map((p, i) =>
                i < rawPercents.length - 1 ? Math.round(p) : p
            );
            const sumRounded = roundedPercents
                .slice(0, rawPercents.length - 1)
                .reduce((a, b) => a + b, 0);
            roundedPercents[rawPercents.length - 1] = 100 - sumRounded;

            return data.labels.map((label, i) => {
              const acronym =
                  data.accronym?.[i] && data.accronym[i] !== "all_populations"
                      ? ` (${data.accronym[i]})`
                      : "";

              return {
                text: `${label}${acronym}: ${dataset.data[i]} ${t(
                    "qvct_survey.survey_report.is"
                )} ${roundedPercents[i]}%`,
                fillStyle: dataset.backgroundColor[i],
                strokeStyle: dataset.borderColor
                    ? dataset.borderColor[i]
                    : dataset.backgroundColor[i],
                hidden:
                    isNaN(dataset.data[i]) ||
                    chart.getDatasetMeta(0).data[i].hidden,
                index: i,
              };
            });
          }
          return [];
        }

      }
    },
    tooltip: {
      callbacks: {
        label: function(context) {
          const dataset = context.dataset;
          const total = dataset.data.reduce((sum, val) => sum + val, 0);
          const value = context.raw;
          const percentage = Math.round((value * 100) / total);
          return `${context.label}: ${value} ${t('qvct_survey.survey_report.is')} (${percentage}%)`;
        }
      }
    }
  }
}

const barOptions = {
  ...chartOptions,
  scales: {
    y: {max:100, beginAtZero: true, ticks: {precision: 0 }, grid: { drawBorder: false } },
    x: {
      grid: { display: false },
      ticks: {
        color: '#000', maxRotation: 0, minRotation: 0, autoSkip: true, autoSkipPadding: 10, font: { size: 10 },
        callback: function(value) { let label = this.getLabelForValue(value); return label.match(/.{1,12}/g); }
      }
    }
  },
  datasets: { bar: { categoryPercentage: 0.5, barPercentage: 0.5, maxBarThickness: 10 } }
}

const getChartData = () => {
  if (!props.surveyReport || !props.selectedCategory) return { labels: [], datasets: [] }
  let populationGroups
  if (props.selectedPopulation === 'false') {
    populationGroups = Object.keys(props.surveyReport).includes('all_populations') ? ['all_populations'] : []
  } else {
    populationGroups = Object.keys(props.surveyReport).filter(key => key !== 'all_populations')
  }
  if (populationGroups.length === 0) return { labels: [], datasets: [] }
  const labels = populationGroups.map(gId => props.surveyReport[gId]?.name || (gId === 'all_populations' ? 'Toutes populations' : gId))
  const accronym = populationGroups.map(gId => props.surveyReport[gId]?.accronym || gId)
  const data = populationGroups.map(gId => props.surveyReport[gId]?.total_respondents || 0)
  const colors = populationGroups.map(gId => props.surveyReport[gId]?.color || '#36a2eb')
  return { labels,accronym, datasets: [{ label: '', data, backgroundColor: colors, barThickness: 25, maxBarThickness: 20 }] }
}

const destroyChart = () => {
  if (chart.value) { try { chart.value.destroy() } catch (e) {} chart.value = null }
  try {
    const canvas = typeof document !== 'undefined' ? document.getElementById(chartId.value) : null
    if (canvas) {
      try { const existing = Chart.getChart && Chart.getChart(canvas); if (existing) existing.destroy() } catch (e) {}
      try { const inst = Chart.instances; const list = Array.isArray(inst) ? inst : (inst ? Object.values(inst) : []); list.forEach(i => { if (i && i.canvas === canvas) { try { i.destroy() } catch (e) {} } }) } catch (e) {}
      try { Object.keys(canvas).forEach(k => { if (k.startsWith('__chart') || k.startsWith('_chart')) canvas[k] = null }) } catch (e) {}
    }
  } catch (e) {}
}

const changeChartType = (newType) => { chartType.value = newType; initChart() }

const initChart = () => {
  destroyChart()
  nextTick(() => {
    const canvas = document.getElementById(chartId.value)
    if (!canvas) return
    try { const existing = Chart.getChart && Chart.getChart(canvas); if (existing) existing.destroy() } catch (e) {}
    const data = getChartData()
    if (data.labels.length === 0) return
    const options = chartType.value === 'doughnut' ? doughnutOptions : barOptions
    try { chart.value = new Chart(canvas.getContext('2d'), { type: chartType.value, data, options }) } catch (error) { console.error('Error creating chart:', error) }
  })
}

watch([() => props.surveyReport, () => props.selectedCategory, () => props.selectedPopulation], () => { initChart() }, { immediate: true })

onMounted(() => { /* watcher will init */ })

onUnmounted(() => { destroyChart() })
</script>

<style scoped>
.chart-container { position: relative; width: 70%;margin-left: 300px}
.btn-icon { background: none; border: none; color: #6c757d; padding: 0.25rem 0.5rem; }
.btn-icon:hover { color: #0d6efd; }
</style>
